import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { ExclamationTriangleIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

interface DeleteConfirmState {
  type: 'lesson' | 'student' | 'expense'
  item: {
    id: string
    name: string
    description?: string
    details?: string[]
  }
  returnPath: string
}

export const DeleteConfirmPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as DeleteConfirmState
  const [confirmText, setConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  // If no state provided, redirect back
  useEffect(() => {
    if (!state?.item) {
      navigate('/admin/lessons', { replace: true })
    }
  }, [state, navigate])

  if (!state?.item) {
    return null // Will redirect in useEffect
  }

  const getTypeInfo = () => {
    const types = {
      lesson: {
        title: 'ลบบทเรียน',
        itemName: 'บทเรียน',
        warningText: 'การลบบทเรียนจะส่งผลต่อ:',
        consequences: [
          'ผู้ปกครองจะไม่สามารถดูบทเรียนนี้ได้อีก',
          'ข้อมูลการบ้านและเนื้อหาจะถูกลบอย่างถาวร',
          'ประวัติการเรียนในระบบจะไม่สมบูรณ์',
          'ไม่สามารถกู้คืนข้อมูลได้อีก'
        ],
        dangerLevel: 'high'
      },
      student: {
        title: 'ลบข้อมูลนักเรียน',
        itemName: 'นักเรียน',
        warningText: 'การลบข้อมูลนักเรียนจะส่งผลต่อ:',
        consequences: [
          'ข้อมูลนักเรียนและผู้ปกครองจะถูกลบทั้งหมด',
          'ประวัติการเรียนและการชำระเงินจะหายไป',
          'ผู้ปกครองจะไม่สามารถเข้าใช้ระบบได้',
          'ข้อมูลที่ลบไปแล้วไม่สามารถกู้คืนได้'
        ],
        dangerLevel: 'critical'
      },
      expense: {
        title: 'ลบรายการค่าใช้จ่าย',
        itemName: 'รายการค่าใช้จ่าย',
        warningText: 'การลบรายการค่าใช้จ่ายจะส่งผลต่อ:',
        consequences: [
          'ข้อมูลการเรียกเก็บเงินจะหายไป',
          'ผู้ปกครองอาจไม่ทราบรายการที่ต้องชำระ',
          'รายงานทางการเงินจะไม่ครบถ้วน',
          'ไม่สามารถติดตามหนี้สินได้'
        ],
        dangerLevel: 'medium'
      }
    }
    return types[state.type]
  }

  const typeInfo = getTypeInfo()
  const confirmationWord = 'ลบ'

  const handleDelete = async () => {
    if (confirmText !== confirmationWord) {
      return
    }

    setIsDeleting(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Navigate to success page
      navigate('/admin/delete/success', {
        state: {
          type: state.type,
          itemName: state.item.name,
          deletedAt: new Date().toISOString(),
          returnPath: state.returnPath
        },
        replace: true
      })
    } catch (error) {
      console.error('Deletion failed:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const canDelete = confirmText === confirmationWord

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title={typeInfo.title}
        showBackButton={true}
        onBack={() => navigate(state.returnPath)}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Warning Message */}
        <div className={`border rounded-lg p-4 mb-6 ${
          typeInfo.dangerLevel === 'critical' ? 'bg-red-50 border-red-200' :
          typeInfo.dangerLevel === 'high' ? 'bg-orange-50 border-orange-200' :
          'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-start">
            <ExclamationTriangleIcon className={`h-5 w-5 mt-0.5 mr-3 flex-shrink-0 ${
              typeInfo.dangerLevel === 'critical' ? 'text-red-600' :
              typeInfo.dangerLevel === 'high' ? 'text-orange-600' :
              'text-yellow-600'
            }`} />
            <div>
              <h3 className={`font-medium mb-1 ${
                typeInfo.dangerLevel === 'critical' ? 'text-red-800' :
                typeInfo.dangerLevel === 'high' ? 'text-orange-800' :
                'text-yellow-800'
              }`}>
                คำเตือน: การดำเนินการนี้ไม่สามารถยกเลิกได้
              </h3>
              <p className={`text-sm ${
                typeInfo.dangerLevel === 'critical' ? 'text-red-700' :
                typeInfo.dangerLevel === 'high' ? 'text-orange-700' :
                'text-yellow-700'
              }`}>
                กรุณาพิจารณาอย่างรอบคอบก่อนดำเนินการ
              </p>
            </div>
          </div>
        </div>

        {/* Item Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {typeInfo.itemName}ที่จะลบ
          </h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ชื่อ:</span>
              <span className="font-medium">{state.item.name}</span>
            </div>
            
            {state.item.description && (
              <div className="flex justify-between items-start">
                <span className="text-gray-600">รายละเอียด:</span>
                <span className="font-medium text-right max-w-64">{state.item.description}</span>
              </div>
            )}

            {state.item.details && state.item.details.length > 0 && (
              <div>
                <span className="text-gray-600 block mb-2">ข้อมูลเพิ่มเติม:</span>
                <div className="space-y-1">
                  {state.item.details.map((detail, index) => (
                    <div key={index} className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Consequences */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="font-medium text-gray-900 mb-3">{typeInfo.warningText}</h3>
          <div className="space-y-2">
            {typeInfo.consequences.map((consequence, index) => (
              <div key={index} className="flex items-start">
                <span className={`w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0 ${
                  typeInfo.dangerLevel === 'critical' ? 'bg-red-500' :
                  typeInfo.dangerLevel === 'high' ? 'bg-orange-500' :
                  'bg-yellow-500'
                }`}></span>
                <span className="text-sm text-gray-700">{consequence}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Confirmation Input */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="font-medium text-gray-900 mb-3">ยืนยันการลบ</h3>
          <p className="text-sm text-gray-600 mb-4">
            พิมพ์คำว่า <span className="font-bold text-red-600">"{confirmationWord}"</span> เพื่อยืนยันการลบ
          </p>
          
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`พิมพ์ "${confirmationWord}" ที่นี่`}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-red-500 ${
              confirmText && confirmText !== confirmationWord 
                ? 'border-red-300 focus:ring-red-500' 
                : 'border-gray-300 focus:ring-red-500'
            }`}
          />
          
          {confirmText && confirmText !== confirmationWord && (
            <p className="text-xs text-red-600 mt-1">
              กรุณาพิมพ์คำว่า "{confirmationWord}" ให้ถูกต้อง
            </p>
          )}
        </div>

        {/* Alternative Actions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">💡 ทางเลือกอื่น</h3>
          <div className="text-sm text-blue-800 space-y-1">
            {state.type === 'lesson' && (
              <>
                <p>• แก้ไขเนื้อหาแทนการลบ</p>
                <p>• ซ่อนไม่ให้ผู้ปกครองเห็น</p>
              </>
            )}
            {state.type === 'student' && (
              <>
                <p>• เปลี่ยนสถานะเป็น "พักเรียน"</p>
                <p>• ระงับการใช้งานชั่วคราว</p>
              </>
            )}
            {state.type === 'expense' && (
              <>
                <p>• แก้ไขจำนวนเงินแทนการลบ</p>
                <p>• เปลี่ยนสถานะเป็น "ยกเลิก"</p>
              </>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(state.returnPath)}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            ยกเลิก
          </button>
          
          <button
            onClick={handleDelete}
            disabled={!canDelete || isDeleting}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
              canDelete && !isDeleting
                ? typeInfo.dangerLevel === 'critical' 
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                กำลังลบ...
              </>
            ) : (
              <>
                <TrashIcon className="h-5 w-5 mr-2" />
                ลบถาวร
              </>
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            หากมีข้อสงสัย กรุณาปรึกษาผู้ดูแลระบบก่อนดำเนินการ
          </p>
        </div>
      </div>
    </div>
  )
}